/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { ActionReducerMapBuilder, createAsyncThunk } from '@reduxjs/toolkit';
import _ from 'lodash';

import { ApiStatus } from '../../../../../ts';
import { FeatureFlag } from '../../../../../ts/feature-flags';
import { TaskListKey } from '../../../../../ts/preferences';
import {
  CreateSkill,
  DialogSystemSettings,
  Language,
  SearchRoutingTarget,
  Skill,
  SkillType
} from '../../../../../ts/skill';
import {
  AppDispatch,
  CreateAsyncThunk,
  RootState,
  SkillsSlice
} from '../../../../../ts/state';
import { getBaseLLMModelData } from '../../../../actions/api/actions';
import { isActionSkill } from '../../../../actions/util/skill';
import {
  getSampleWorkspace,
  getStarterWorkspace
} from '../../../../dialog/store/workspaces/actions';
import * as API from '../../../api/skills';
import { getErrorString } from '../../../util/errorHandler';
import { createdObjectEvents } from '../../../util/trackers/segment';
import { getAssistant } from '../../assistants/actions';
import {
  isFeatureEnabled,
  isFeatureEnabledForLanguage,
  selectShouldCreatedSkillHaveBaseLLMEnabled,
  selectShouldCreatedSkillHaveHAPFilteringEnabled
} from '../../features/selectors';
import { selectIsNewIa } from '../../instance/selectors';
import { addErrorMessage } from '../../messages/actions';
import { getAssistantId } from '../../session/selectors';
import { entityAdapter } from '../adapter';
import { getSkillType, toSkill } from '../helper';

/**
 * Creates a new skill. This action will fire other sub actions to load
 * default workspace content or skill content to pre-populate the skill.
 * Also looks at the features enabled and adds those by default if needed.
 * @param {string} params.skill metadata such as name, description and language for the skill (as entered by the end user)
 * @param {boolean} params.sample whether or not the skill should be created from the sample skill
 *
 * @returns {AsyncThunk} The async action
 */
export const createSkill = createAsyncThunk<
  Skill,
  CreateSkill,
  CreateAsyncThunk
>('skills/create', async (params, { dispatch, getState }) => {
  try {
    let skill = params;

    if (skill.description) {
      // If the skill has a description, make sure there are no new line, carriage return,
      // or tab characters in it or else an error will get returned
      skill.description = skill?.description.replace(/[\r\n\t]/g, ' ');
    }

    if (skill.type === SkillType.DIALOG || skill.type === SkillType.ACTION) {
      // If the new skill is dialog or action we need to populate the user provided data with
      // the sample content or features that are enabled for the instance like spellchecker
      skill = await configureDialogOrActionSkill(skill, getState, dispatch);
    }

    const new_ia = selectIsNewIa(getState());
    const result = await API.createSkill(skill, { new_ia });
    // Notify Analytics
    createdObjectEvents.skill(
      {
        object: result.skill_id,
        resultValue: 'success',
        successFlag: true
      },
      {
        skillType: getSkillType(result)
      }
    );
    if (new_ia) {
      const assistantId = getAssistantId(getState());
      if (assistantId) {
        await dispatch(getAssistant({ assistantId })); //load assistant so it will have latest `skill_references`
      }
    }
    if (result?.skill_id && !result?.workspace_id) {
      //for legacy reasons workspaces need to have a workspace_id, if one is not set just reuse the skill_id
      result.workspace_id = result.skill_id;
    }
    return result;
  } catch (error) {
    dispatch(addErrorMessage(error));
    createdObjectEvents.skill({
      object: null,
      resultValue: getErrorString(error),
      successFlag: false
    });
    throw error;
  }
});

/**
 * Configures a skill that will be created based on the available feature flags
 *
 * @param {object} skill The data to create the skill
 * @param {*} getState The redux getState method
 * @param {*} dispatch The redux dispatch method
 * @returns {object} The skill configured skill
 */
export const configureDialogOrActionSkill = async (
  skill: CreateSkill,
  getState: () => RootState,
  dispatch: AppDispatch
): Promise<CreateSkill> => {
  const { sample } = skill;
  delete skill.sample;
  const language = skill.language;

  if (!sample) {
    // add default content to the skill
    const suffix = `${skill.type}-${language}`;
    const path = `models.default_content.${suffix}`;
    let defaultWorkspace = _.get(getState(), path, null);

    if (!defaultWorkspace) {
      //default content is not in redux - load it
      await dispatch(getStarterWorkspace(skill.type, language));
      defaultWorkspace = _.get(getState(), path, null);
    }

    if (defaultWorkspace) {
      const defaultSkill = toSkill(defaultWorkspace);
      // Merge the user provided content with the default content
      skill = { ...defaultSkill, ...skill };
    }

    if (skill.type === SkillType.ACTION) {
      // actions skills can't have dialog nodes
      if (skill.workspace) {
        skill.workspace.dialog_nodes = [];
      }
    }
  } else {
    // add default content to the skill
    const isActions = isActionSkill(skill);
    const path = `models.sample_content.${
      isActions ? 'actions' : 'dialog'
    }-${language}`;
    let sampleContent = _.get(getState(), path, null);

    if (!sampleContent) {
      await dispatch(getSampleWorkspace(language, isActions));
      sampleContent = _.get(getState(), path, null);
    }

    if (sampleContent) {
      const defaultSkill = isActions ? sampleContent : toSkill(sampleContent);
      skill = { ...defaultSkill, ...skill };
    }
  }

  // Add features if enabled via feature flag
  const state = getState();

  const systemSettings = await configureSystemSettings(
    skill.workspace.system_settings,
    language,
    state
  );

  skill.workspace.system_settings = systemSettings;

  return skill;
};

/**
 * Configures the `system_settings` for a workspace based on the available features for the given `language`.
 * @param {*} systemSettings The skill system settings
 * @param {*} language The language. Default to `en`
 * @param {*} state The redux State
 * @returns {object} The `system_settings` to use in the skill
 */
export const configureSystemSettings = async (
  systemSettings: Partial<DialogSystemSettings> = {},
  language: Language = Language.English,
  state: RootState
): Promise<DialogSystemSettings> => {
  const spellCheckDefault = isFeatureEnabledForLanguage(
    state,
    language,
    FeatureFlag.SPELL_CHECK_DEFAULT
  );

  const offTopic = isFeatureEnabledForLanguage(
    state,
    language,
    FeatureFlag.OFF_TOPIC_V2
  );

  const isLaunchModesEnabled = isFeatureEnabled(
    state,
    FeatureFlag.ACTIONS_NEW_IA_LAUNCH_MODES
  );
  const isGenerativeAIEnabled = isFeatureEnabled(
    state,
    FeatureFlag.ACTIONS_GENERATIVE_AI
  );

  const defaultFeatures: Partial<DialogSystemSettings> = {};
  if (spellCheckDefault) {
    defaultFeatures.spelling_auto_correct = true;
  }

  if (offTopic || language === Language.English) {
    defaultFeatures.off_topic = { enabled: true };
  }

  const shouldCreatedSkillHaveBaseLLMEnabled =
    selectShouldCreatedSkillHaveBaseLLMEnabled(state, language);
  const shouldCreatedSkillHaveHAPFilteringEnabled =
    selectShouldCreatedSkillHaveHAPFilteringEnabled(state, language);
  const generativeAiModels =
    (await getBaseLLMModelData(TaskListKey.TEXT_GENERATION)).data?.models || [];
  const defaultModel = generativeAiModels.find(model => model.recommended);

  if (isGenerativeAIEnabled) {
    // If generative AI flag is enabled and base LLM should be enabled by default,
    // set base model to the recommended model from store
    if (shouldCreatedSkillHaveBaseLLMEnabled) {
      defaultFeatures.generative_ai = {
        ...defaultFeatures.generative_ai,
        model_id: defaultModel?.model_id || 'ibm/granite-3-8b-instruct'
      };

      // If generative AI flag is enabled and base LLM should be enabled by default, set default
      // search routing target to conversational search
      defaultFeatures.search_routing = {
        ...defaultFeatures.search_routing,
        target: SearchRoutingTarget.SEARCH
      };
    }

    // if we should default HAP filtering to being
    // enabled, do so
    if (shouldCreatedSkillHaveHAPFilteringEnabled) {
      defaultFeatures.generative_ai = {
        ...defaultFeatures.generative_ai,
        hap_filter: {
          output: {
            enabled: true
          }
        }
      };
    }
  }

  const use_connect_to_support_prompt = isLaunchModesEnabled
    ? _.get(
        systemSettings,
        'disambiguation.use_connect_to_support_prompt',
        'Connect to support'
      )
    : undefined;

  defaultFeatures.disambiguation = {
    enabled: true,
    suggestion_text_policy: 'title',
    randomize: true,
    prompt: _.get(systemSettings, 'disambiguation.prompt', 'Did you mean:'),
    max_suggestions: 5,
    none_of_the_above_prompt: _.get(
      systemSettings,
      'disambiguation.none_of_the_above_prompt',
      'None of the above'
    ),
    use_connect_to_support_prompt,
    single_answer_clarification_prompt: _.get(
      systemSettings,
      'disambiguation.single_answer_clarification_prompt',
      'Something else'
    )
  };
  return { ...systemSettings, ...defaultFeatures };
};

const reducer = (builder: ActionReducerMapBuilder<SkillsSlice>) => {
  builder
    .addCase(createSkill.pending, (state, _action) => {
      state.createStatus = ApiStatus.LOADING;
      state.createError = null;
    })
    .addCase(createSkill.fulfilled, (state, action) => {
      state.createStatus = ApiStatus.SUCCEEDED;
      state.createError = null;
      entityAdapter.addOne(state, action.payload);
    })
    .addCase(createSkill.rejected, (state, action) => {
      state.createStatus = ApiStatus.ERROR;
      state.createError = action.error;
    });
};

export default reducer;
